import { Link } from 'react-router-dom';
import { 
  FileText,
  UserCheck,
  Car,
  Rocket,
  ChevronRight,
  CheckCircle2
} from 'lucide-react';
import SEO from '@/components/SEO';

const HowItWorksPage = () => {
  const steps = [
    {
      number: "01",
      icon: <FileText className="w-8 h-8" />,
      title: "Soumettez votre demande",
      description: "Remplissez notre formulaire en ligne avec vos informations et votre profil VTC. C'est rapide et sans engagement.",
      details: [
        "Formulaire simple et rapide",
        "Aucun engagement initial",
        "Réponse sous 24h"
      ]
    },
    {
      number: "02",
      icon: <UserCheck className="w-8 h-8" />,
      title: "Nous vérifions votre profil",
      description: "Notre équipe examine votre dossier et vérifie votre statut de chauffeur VTC. Un processus simplifié pour vous faciliter la tâche.",
      details: [
        "Vérification rapide",
        "Accompagnement personnalisé",
        "Conditions flexibles"
      ]
    },
    {
      number: "03",
      icon: <Car className="w-8 h-8" />,
      title: "Véhicule disponible",
      description: "Une fois votre dossier validé, votre Tesla Model Y est préparée et mise à votre disposition rapidement.",
      details: [
        "Véhicule inspecté et nettoyé",
        "Formation à l'utilisation",
        "Remise des clés à Marseille"
      ]
    },
    {
      number: "04",
      icon: <Rocket className="w-8 h-8" />,
      title: "Commencez à rouler",
      description: "Prenez le volant et démarrez votre activité VTC avec un véhicule premium qui impressionnera vos clients.",
      details: [
        "Support continu",
        "Assistance 24h/24",
        "Flexibilité totale"
      ]
    }
  ];

  const faqs = [
    {
      question: "Quels documents sont nécessaires ?",
      answer: "Vous aurez besoin de votre carte VTC, permis de conduire, pièce d'identité et justificatif de domicile."
    },
    {
      question: "Quelle est la durée minimum de location ?",
      answer: "La location minimum est d'une semaine, avec possibilité de renouvellement flexible."
    },
    {
      question: "L'assurance est-elle incluse ?",
      answer: "Oui, l'assurance tous risques est incluse dans le tarif de location."
    },
    {
      question: "Comment fonctionne la recharge ?",
      answer: "Une carte de recharge est fournie, vous donnant accès au réseau de Superchargeurs Tesla et aux bornes partenaires."
    }
  ];

  return (
    <div data-testid="how-it-works-page" className="overflow-hidden">
      <SEO 
        title="Comment Louer un Véhicule VTC | Processus Simple en 4 Étapes | Luxeo Car Marseille"
        description="Louez un véhicule premium pour votre activité VTC en 4 étapes simples. Demande en ligne, validation rapide, véhicule disponible. Tesla Model Y, Mustang Mach-E, Mercedes Classe V à Marseille."
        keywords="comment louer véhicule VTC, location voiture Uber, processus location VTC, chauffeur VTC Marseille, démarrer activité VTC"
        canonical="/comment-ca-marche"
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block font-['Outfit'] text-[#D4AF37] text-sm font-medium uppercase tracking-wider mb-4">
              Processus Simplifié
            </span>
            <h1 
              data-testid="how-hero-title"
              className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
            >
              Comment ça{' '}
              <span className="text-gradient-gold">marche</span> ?
            </h1>
            <p className="font-['Manrope'] text-base lg:text-lg text-[#A1A1AA]">
              Un processus simple en 4 étapes pour démarrer votre activité VTC avec une Tesla Model Y premium.
            </p>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section data-testid="steps-section" className="section-padding bg-[#0A0A0A] relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-0">
            {steps.map((step, index) => (
              <div
                key={index}
                data-testid={`step-${index + 1}`}
                className={`relative ${index !== steps.length - 1 ? 'pb-16' : ''}`}
              >
                {/* Connector Line */}
                {index !== steps.length - 1 && (
                  <div className="absolute left-[39px] top-[80px] bottom-0 w-px bg-gradient-to-b from-[#D4AF37]/50 to-transparent hidden md:block" />
                )}

                <div className="flex flex-col md:flex-row gap-8 items-start">
                  {/* Step Number & Icon */}
                  <div className="flex-shrink-0 relative">
                    <div className="w-20 h-20 rounded-2xl bg-[#121212] border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] relative z-10">
                      {step.icon}
                    </div>
                    <span className="absolute -top-3 -right-3 font-['Outfit'] text-5xl font-bold text-[#D4AF37]/10">
                      {step.number}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="font-['Outfit'] text-2xl font-bold text-white mb-3">
                      {step.title}
                    </h3>
                    <p className="font-['Manrope'] text-[#A1A1AA] mb-6 max-w-xl leading-relaxed">
                      {step.description}
                    </p>
                    <div className="flex flex-wrap gap-4">
                      {step.details.map((detail, detailIndex) => (
                        <div
                          key={detailIndex}
                          className="flex items-center gap-2 px-4 py-2 rounded-full glass-light"
                        >
                          <CheckCircle2 className="w-4 h-4 text-[#D4AF37]" />
                          <span className="font-['Manrope'] text-white/80 text-sm">
                            {detail}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section data-testid="faq-section" className="section-padding bg-[#050505]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white mb-4">
              Questions <span className="text-[#D4AF37]">fréquentes</span>
            </h2>
            <p className="font-['Manrope'] text-[#A1A1AA]">
              Tout ce que vous devez savoir avant de commencer.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                data-testid={`faq-${index}`}
                className="card-luxeo p-6"
              >
                <h3 className="font-['Outfit'] text-lg font-semibold text-white mb-3">
                  {faq.question}
                </h3>
                <p className="font-['Manrope'] text-[#A1A1AA] text-sm leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="how-cta-section" className="section-padding bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="card-luxeo p-10 sm:p-16 gold-glow">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white mb-6">
              Prêt à commencer ?
            </h2>
            <p className="font-['Manrope'] text-[#A1A1AA] mb-10 max-w-xl mx-auto">
              Faites le premier pas vers une activité VTC premium avec la Tesla Model Y.
              Notre équipe vous accompagne à chaque étape.
            </p>
            <Link
              to="/contact"
              data-testid="how-final-cta"
              className="btn-primary text-lg font-bold px-10 py-5 inline-flex items-center gap-2 group"
            >
              Faire une demande de location
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;
